import axios from 'axios'
export { axiosInstance }
import type {
  AskQuestionRequest,
  AskQuestionResponse,
  SessionResponse,
  MessageResponse,
} from '@/types'

// ─────────────────────────────────────────────
// api.ts
// All REST calls to DirayahAI.API backend.
// baseURL is empty in dev (Vite proxy handles it).
// In production the iFrame receives apiUrl param
// from loader.js and we set it here.
// ─────────────────────────────────────────────

// Called once from App.tsx after config is resolved
export function initApiBaseUrl(apiUrl: string): void {
  if (apiUrl) {
    axiosInstance.defaults.baseURL = apiUrl
  }
}

const axiosInstance = axios.create({
  // In dev: empty = Vite proxy handles routing
  // In production: fallback to Railway API if no apiUrl param passed via loader.js
  baseURL: import.meta.env.VITE_API_URL ?? '',
  timeout: 30000,        // 30s — LLM responses can be slow on first token
  headers: {
    'Content-Type': 'application/json',
  },
})

// ── Auth token injection ───────────────────────
// Widget operates anonymously (no login required).
// If a JWT is ever passed via URL param for
// authenticated tenants, inject it here.
let _token: string | null = null

export function setAuthToken(token: string | null): void {
  _token = token
  if (token) {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete axiosInstance.defaults.headers.common['Authorization']
  }
}

export function getAuthToken(): string | null {
  return _token
}

// ── Chat endpoints ────────────────────────────

// REST fallback — used when SignalR is unavailable
export async function askQuestion(
  request: AskQuestionRequest
): Promise<AskQuestionResponse> {
  const { data } = await axiosInstance.post<AskQuestionResponse>(
    '/api/chat/ask',
    request
  )
  return data
}

// Load all sessions for the current tenant+domain
export async function getSessions(
  tenantId: string,
  domainId: string
): Promise<SessionResponse[]> {
  const { data } = await axiosInstance.get<SessionResponse[]>(
    '/api/chat/sessions',
    { params: { tenantId, domainId } }
  )
  return data
}

// Load all messages for a specific session
export async function getMessages(
  sessionId: string
): Promise<MessageResponse[]> {
  const { data } = await axiosInstance.get<MessageResponse[]>(
    `/api/chat/sessions/${sessionId}/messages`
  )
  return data
}

// Update session title (auto-generated from first message)
export async function updateSessionTitle(
  sessionId: string,
  title: string
): Promise<void> {
  await axiosInstance.patch(
    `/api/chat/sessions/${sessionId}/title`,
    { title }
  )
}

// Fetch predefined suggested questions for the domain
export async function getSuggestedQuestions(domainId: string): Promise<string[]> {
  try {
    const { data } = await axiosInstance.get<{ data: { id: string; question: string }[] }>(
      `/api/chat/domains/${domainId}/suggested-questions`
    )
    return data.data.map((q) => q.question)
  } catch {
    return []
  }
}

// Which action buttons this domain wants shown. Returns a digits-only WhatsApp
// number ready for a wa.me link, or null when the business hasn't enabled it.
export async function getWidgetSettings(
  domainId: string
): Promise<{ showWhatsAppButton: boolean; whatsAppNumber: string | null }> {
  try {
    const { data } = await axiosInstance.get<{
      showWhatsAppButton: boolean
      whatsAppNumber: string | null
    }>(`/api/domains/${domainId}/widget-settings`)
    return data
  } catch {
    return { showWhatsAppButton: false, whatsAppNumber: null }
  }
}

// Records that a visitor clicked through to WhatsApp. Fired in the background —
// never awaited before opening WhatsApp, so a failure here can't block the visitor.
export async function logWhatsAppHandoff(
  domainId: string,
  payload: {
    sessionId: string | null
    refCode: string
    lastQuestion: string
    messageCount: number
    pageUrl: string
  }
): Promise<void> {
  await axiosInstance.post(`/api/domains/${domainId}/whatsapp-handoff`, payload)
}

// Email the chat transcript to the provided address
export async function emailTranscript(sessionId: string, email: string): Promise<void> {
  await axiosInstance.post(`/api/chat/sessions/${sessionId}/email-transcript`, { email })
}

// Widget authentication — called once on load
// Exchanges WidgetApiKey for a short-lived JWT
export async function widgetLogin(
  widgetApiKey: string,
  domainId: string
): Promise<{ token: string; tenantId: string; domainId: string }> {
  const { data } = await axiosInstance.post<{
    accessToken: string
    expiresInSeconds: number
    tenantId: string
    domainId: string
  }>('/api/auth/widget', { widgetApiKey, domainId })

  return {
    token:    data.accessToken,
    tenantId: data.tenantId,
    domainId: data.domainId,
  }
}